import * as React from "react";
import { Color, Group, Vector3, MeshLambertMaterial, Matrix4 } from "three";
import { MaterialNode, applyProps } from "@react-three/fiber";
import { v4 } from "uuid";
import { CloudState, CloudProps } from "../types";
import { Clouds } from "./Clouds";
import { CloudStateContext } from "../context/CloudState";

const context = CloudStateContext;

declare global {
  namespace JSX {
    interface IntrinsicElements {
      cloudMaterial: MaterialNode<
        MeshLambertMaterial,
        typeof MeshLambertMaterial
      >;
    }
  }
}

const CloudInstance = React.forwardRef<Group, CloudProps>(
  (
    {
      opacity = 1,
      speed = 0,
      bounds = [5, 1, 1],
      segments = 20,
      color = "#ffffff",
      fade = 10,
      volume = 6,
      smallestVolume = 0.25,
      distribute = null,
      growth = 4,
      concentrate = "inside",
      seed = Math.random(),
      ...props
    },
    fref
  ) => {
    function random() {
      const x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    }

    const parent = React.useContext(context);
    const ref = React.useRef<Group>(null!);
    const [uuid] = React.useState(() => v4());
    const clouds: CloudState[] = React.useMemo(() => {
      return [...new Array(segments)].map(
        (_, index) =>
          ({
            segments,
            bounds: new Vector3(1, 1, 1),
            position: new Vector3(),
            uuid,
            index,
            ref,
            dist: 0,
            matrix: new Matrix4(),
            color: new Color(),
            rotation: index * (Math.PI / segments),
          } as CloudState)
      );
    }, [segments, uuid]);

    React.useLayoutEffect(() => {
      clouds.forEach((cloud, index) => {
        applyProps(cloud as any, {
          volume,
          color,
          speed,
          growth,
          opacity,
          fade,
          bounds,
          density: Math.max(0.5, random()),
          rotationFactor: Math.max(0.2, 0.5 * random()) * speed,
        });
        // Only distribute randomly if there are multiple segments

        const distributed = distribute?.(cloud, index);

        if (distributed || segments > 1)
          cloud.position.copy(cloud.bounds).multiply(
            distributed?.point ??
              ({
                x: random() * 2 - 1,
                y: random() * 2 - 1,
                z: random() * 2 - 1,
              } as Vector3)
          );
        const xDiff = Math.abs(cloud.position.x);
        const yDiff = Math.abs(cloud.position.y);
        const zDiff = Math.abs(cloud.position.z);
        const max = Math.max(xDiff, yDiff, zDiff);
        cloud.length = 1;
        if (xDiff === max) cloud.length -= xDiff / cloud.bounds.x;
        if (yDiff === max) cloud.length -= yDiff / cloud.bounds.y;
        if (zDiff === max) cloud.length -= zDiff / cloud.bounds.z;
        cloud.volume =
          (distributed?.volume !== undefined
            ? distributed.volume
            : Math.max(
                Math.max(0, smallestVolume),
                concentrate === "random"
                  ? random()
                  : concentrate === "inside"
                  ? cloud.length
                  : 1 - cloud.length
              )) * volume;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clouds/distribute/random/smallestVolume intentionally omitted to avoid unnecessary re-runs
    }, [
      concentrate,
      bounds,
      fade,
      color,
      opacity,
      growth,
      volume,
      seed,
      segments,
      speed,
    ]);

    React.useLayoutEffect(() => {
      const temp = clouds;
      parent.current = [...parent.current, ...temp];
      return () => {
        parent.current = parent.current.filter((item) => item.uuid !== uuid);
      };
    }, [clouds, parent, uuid]);

    React.useImperativeHandle(fref, () => ref.current, []);
    return <group ref={ref} {...props} />;
  }
);

export const Cloud = React.forwardRef<Group, CloudProps>((props, fref) => {
  const parent = React.useContext(context);
  if (parent) return <CloudInstance ref={fref} {...props} />;
  else
    return (
      <Clouds>
        <CloudInstance ref={fref} {...props} />
      </Clouds>
    );
});
